import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { PollCard } from '@/components/PollCard';
import { FullScreenPoll } from '@/components/FullScreenPoll';
import { CreatePollDialog } from '@/components/CreatePollDialog';
import { LoginArea } from '@/components/auth/LoginArea';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { usePolls } from '@/hooks/usePolls';
import { usePoll } from '@/hooks/usePolls';
import { useMyPolls } from '@/hooks/useMyPolls';
import { usePollResults } from '@/hooks/usePollResults';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { BarChart3, User, Flame, Zap } from 'lucide-react';
import { genUserName } from '@/lib/genUserName';

type TimeFilter = '1h' | '12h' | '24h' | '7d' | '30d' | 'mine';

export function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pollId = searchParams.get('poll');

  const { user } = useCurrentUser();
  const author = useAuthor(user?.pubkey || '');
  const [filter, setFilter] = useState<TimeFilter>('24h');
  const { data: allPolls, isLoading: isLoadingAll } = usePolls(100);
  const { data: myPolls, isLoading: isLoadingMine } = useMyPolls(user?.pubkey, 50);

  // Fetch specific poll if pollId is in query params
  const { data: sharedPoll, isLoading: isLoadingSharedPoll } = usePoll(pollId || '');
  const sharedPollResults = usePollResults(sharedPoll, undefined);

  // Set up Open Graph meta tags
  const ogImageUrl = `${window.location.origin}/og-image.svg`;
  const totalVotes = sharedPollResults.data?.totalVotes || 0;
  const pollUrl = sharedPoll ? `${window.location.origin}?poll=${sharedPoll.id}` : window.location.origin;

  const generateDescription = () => {
    if (!sharedPoll) return 'Create and vote on polls using the Nostr protocol. Censorship-resistant, real-time polling powered by NIP-88.';

    const options = sharedPoll.options.map(opt => {
      const voteCount = sharedPollResults.data?.optionCounts[opt.id] || 0;
      const percentage = totalVotes > 0 ? ((voteCount / totalVotes) * 100).toFixed(0) : '0';
      return `${opt.label}: ${percentage}%`;
    }).join(' • ');

    return `${totalVotes} ${totalVotes === 1 ? 'vote' : 'votes'} • ${options}`;
  };

  useSeoMeta({
    title: sharedPoll ? `${sharedPoll.question} - Pollstr` : 'Pollstr - Decentralized Polls on Nostr',
    description: generateDescription(),
    ogTitle: sharedPoll ? `🗳️ ${sharedPoll.question}` : 'Pollstr - Decentralized Polls on Nostr',
    ogDescription: generateDescription(),
    ogUrl: pollUrl,
    ogType: 'website',
    ogImage: ogImageUrl,
    ogImageWidth: '1200',
    ogImageHeight: '630',
    twitterCard: 'summary_large_image',
    twitterTitle: sharedPoll ? `🗳️ ${sharedPoll.question}` : 'Pollstr - Decentralized Polls on Nostr',
    twitterDescription: generateDescription(),
    twitterImage: ogImageUrl,
  });

  const handleBackToFeed = () => {
    setSearchParams({});
  };

  const metadata = author.data?.metadata;
  const displayName = metadata?.name || metadata?.display_name || genUserName(user?.pubkey || '');
  const avatarUrl = metadata?.picture;

  const isLoading = filter === 'mine' ? isLoadingMine : isLoadingAll;

  // Header Component
  const Header = () => (
    <div className="sticky top-0 z-50 glass dark:glass-dark border-b border-orange-500/20 dark:border-orange-500/30 shadow-lg shadow-orange-500/5">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 group cursor-default">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl blur-lg opacity-60 group-hover:opacity-100 transition-opacity duration-500 animate-breathe" />
              <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 p-2.5 rounded-xl shadow-lg group-hover:shadow-orange-500/50 transition-all duration-300 group-hover:scale-110">
                <Flame className="h-6 w-6 text-white animate-fire-dance" />
              </div>
            </div>
            <h1 className="text-2xl font-black tracking-tight">
              <span className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-shift">
                Pollstr
              </span>
            </h1>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <CreatePollDialog />

            {user ? (
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 rounded-full blur opacity-0 group-hover:opacity-60 transition-all duration-500" />
                <Avatar className="h-11 w-11 ring-2 ring-orange-500/40 border-2 border-orange-500/20 cursor-pointer hover:ring-orange-500 transition-all duration-300 relative hover:scale-110">
                  <AvatarImage src={avatarUrl} alt={displayName} />
                  <AvatarFallback className="bg-gradient-to-br from-orange-500 to-amber-600 text-white font-bold text-lg">
                    {displayName[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
            ) : (
              <div className="animate-scale-in">
                <LoginArea className="min-w-[120px]" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <div className="space-y-6">
      {[1, 2, 3].map((i) => (
        <div 
          key={i} 
          className="glass dark:glass-dark rounded-2xl p-6 shadow-xl border border-orange-500/10 dark:border-orange-500/20"
          style={{ animationDelay: `${i * 0.1}s` }}
        >
          <div className="space-y-4 animate-pulse">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full bg-orange-500/10" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32 bg-orange-500/10" />
                <Skeleton className="h-3 w-24 bg-orange-500/10" />
              </div>
            </div>
            <Skeleton className="h-6 w-3/4 bg-orange-500/10" />
            <div className="space-y-3">
              <Skeleton className="h-16 w-full rounded-xl bg-orange-500/10" />
              <Skeleton className="h-16 w-full rounded-xl bg-orange-500/10" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  // Empty State
  const EmptyState = ({ message }: { message: string }) => (
    <div className="glass dark:glass-dark rounded-3xl shadow-2xl border border-orange-500/20 dark:border-orange-500/30 animate-bounce-in overflow-hidden">
      <div className="relative py-20 px-8 text-center">
        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-amber-500/5" />
        
        <div className="relative max-w-sm mx-auto space-y-8">
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-orange-500/30 rounded-full blur-3xl animate-breathe" />
              <div className="relative glass dark:glass-dark p-10 rounded-full border-2 border-orange-500/30 shadow-2xl shadow-orange-500/20">
                <BarChart3 className="h-20 w-20 text-orange-500 animate-bounce-subtle" />
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-3xl font-black bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 bg-clip-text text-transparent">
              No polls found
            </h3>
            <p className="text-foreground/60 font-medium text-lg">
              {message}
            </p>
          </div>
          {user && <CreatePollDialog />}
        </div>
      </div>
    </div>
  );

  // If viewing a shared poll, show fullscreen mode directly
  if (pollId) {
    if (isLoadingSharedPoll) {
      // Show a premium loading state for shared polls
      return (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950 to-black" />
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-orange-500/20 rounded-full blur-[150px] animate-float-slow" />
            <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-amber-500/15 rounded-full blur-[120px] animate-float" />
          </div>
          <div className="relative text-center space-y-6">
            <div className="relative">
              <div className="absolute inset-0 bg-orange-500/30 rounded-full blur-3xl animate-breathe" />
              <div className="relative bg-black/50 p-8 rounded-full border-2 border-orange-500/30">
                <Flame className="h-16 w-16 text-orange-500 animate-fire-dance" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-2xl font-black text-white">Loading Poll</h2>
              <p className="text-white/50">Fetching from the Nostr network...</p>
            </div>
            <div className="flex justify-center">
              <div className="h-1 w-32 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full w-1/2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full animate-shimmer" />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (!sharedPoll) {
      // Show a premium 404 state for missing polls
      return (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-950 to-black" />
            <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-[150px] animate-float-slow" />
          </div>
          <div className="relative text-center space-y-8 px-4">
            <div className="relative">
              <div className="absolute inset-0 bg-orange-500/20 rounded-full blur-3xl animate-breathe" />
              <div className="relative bg-black/50 p-8 rounded-full border-2 border-orange-500/20">
                <BarChart3 className="h-16 w-16 text-orange-500/50" />
              </div>
            </div>
            <div className="space-y-3">
              <h2 className="text-4xl font-black bg-gradient-to-r from-orange-500 to-amber-500 bg-clip-text text-transparent">
                Poll Not Found
              </h2>
              <p className="text-white/50 max-w-md mx-auto">
                This poll doesn't exist or couldn't be loaded from the Nostr relays.
              </p>
            </div>
            <Button 
              onClick={handleBackToFeed} 
              className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:shadow-xl hover:shadow-orange-500/30 transition-all duration-300 font-bold border-0 text-white px-8"
            >
              Browse All Polls
            </Button>
          </div>
        </div>
      );
    }

    // Show fullscreen poll view for shared polls
    return <FullScreenPoll poll={sharedPoll} onClose={handleBackToFeed} />;
  }

  const getTimeFilterInSeconds = (timeFilter: TimeFilter): number => {
    const now = Date.now() / 1000;
    switch (timeFilter) {
      case '1h': return now - (60 * 60);
      case '12h': return now - (12 * 60 * 60);
      case '24h': return now - (24 * 60 * 60);
      case '7d': return now - (7 * 24 * 60 * 60);
      case '30d': return now - (30 * 24 * 60 * 60);
      default: return 0;
    }
  };

  const filteredPolls = (() => {
    if (filter === 'mine') {
      return myPolls || [];
    }

    const timeThreshold = getTimeFilterInSeconds(filter);
    return (allPolls || []).filter(poll => poll.created_at >= timeThreshold);
  })();

  return (
    <div className="min-h-screen relative overflow-hidden bg-background dark">
      {/* Animated Gradient Background */}
      <div className="fixed inset-0 -z-10 gradient-mesh dark:gradient-fire opacity-50" />
      <div className="fixed inset-0 -z-10 bg-gradient-to-b from-transparent via-background/50 to-background" />

      {/* Floating Orbs with Orange/Amber theme */}
      <div className="fixed top-20 right-20 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl animate-float -z-10" />
      <div className="fixed bottom-20 left-20 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl animate-float-slow -z-10" style={{ animationDelay: '1s' }} />
      <div className="fixed top-1/2 left-1/2 w-80 h-80 bg-orange-600/10 rounded-full blur-3xl animate-ember-float -z-10" style={{ animationDelay: '2s' }} />

      {/* Header */}
      <Header />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 pb-24">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Filter Tabs */}
          <div className="glass dark:glass-dark rounded-2xl p-4 shadow-2xl border border-orange-500/20 dark:border-orange-500/30 animate-slide-up hover:shadow-[0_20px_60px_rgba(255,140,0,0.15)] transition-all duration-500">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as TimeFilter)}>
              <TabsList className="glass dark:glass-dark border border-orange-500/20 flex-wrap h-auto gap-2 p-2 shadow-lg bg-transparent">
                <TabsTrigger 
                  value="1h" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/20 data-[state=active]:to-amber-500/20 data-[state=active]:text-orange-500 data-[state=active]:shadow-md data-[state=active]:border-orange-500/40 font-bold transition-all duration-300 hover:text-orange-500"
                >
                  Last Hour
                </TabsTrigger>
                <TabsTrigger 
                  value="12h" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/20 data-[state=active]:to-amber-500/20 data-[state=active]:text-orange-500 data-[state=active]:shadow-md data-[state=active]:border-orange-500/40 font-bold transition-all duration-300 hover:text-orange-500"
                >
                  12 Hours
                </TabsTrigger>
                <TabsTrigger 
                  value="24h" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/20 data-[state=active]:to-amber-500/20 data-[state=active]:text-orange-500 data-[state=active]:shadow-md data-[state=active]:border-orange-500/40 font-bold transition-all duration-300 hover:text-orange-500"
                >
                  24 Hours
                </TabsTrigger>
                <TabsTrigger 
                  value="7d" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/20 data-[state=active]:to-amber-500/20 data-[state=active]:text-orange-500 data-[state=active]:shadow-md data-[state=active]:border-orange-500/40 font-bold transition-all duration-300 hover:text-orange-500"
                >
                  Week
                </TabsTrigger>
                <TabsTrigger 
                  value="30d" 
                  className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/20 data-[state=active]:to-amber-500/20 data-[state=active]:text-orange-500 data-[state=active]:shadow-md data-[state=active]:border-orange-500/40 font-bold transition-all duration-300 hover:text-orange-500"
                >
                  Month
                </TabsTrigger>
                {user && (
                  <TabsTrigger 
                    value="mine" 
                    className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500/20 data-[state=active]:to-amber-500/20 data-[state=active]:text-orange-500 data-[state=active]:shadow-md data-[state=active]:border-orange-500/40 font-bold transition-all duration-300 hover:text-orange-500"
                  >
                    <User className="h-4 w-4 mr-2" />
                    My Polls
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          </div>

          {/* Loading State */}
          {isLoading && <LoadingSkeleton />}

          {/* Polls List */}
          {!isLoading && filteredPolls && filteredPolls.length > 0 && (
            <div className="space-y-6">
              {filteredPolls.map((poll, index) => (
                <div
                  key={poll.id}
                  className="animate-slide-up-bold"
                  style={{ animationDelay: `${index * 0.08}s` }}
                >
                  <PollCard poll={poll} />
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && (!filteredPolls || filteredPolls.length === 0) && (
            <EmptyState 
              message={filter === 'mine' 
                ? "You haven't created any polls yet." 
                : 'No polls found in this time range. Try a different filter!'
              } 
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-orange-500/20 dark:border-orange-500/30 mt-12 py-8 glass dark:glass-dark">
        <div className="container mx-auto px-4 text-center space-y-3">
          <p className="text-sm text-foreground/50 flex items-center justify-center gap-2">
            App Vibed with Love By{' '}
            <a
              href="https://uno.dog"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-500 hover:text-amber-500 transition-colors font-bold hover:underline inline-flex items-center gap-1.5 group"
            >
              Uno
              <Zap className="h-3.5 w-3.5 fill-current group-hover:animate-wiggle" />
            </a>
          </p>
          <p className="text-xs text-foreground/30">
            Vibed with{' '}
            <a 
              href="https://shakespeare.diy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-orange-500/70 hover:text-orange-500 transition-colors"
            >
              Shakespeare
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
